import { Paper, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridCellValue, GridSortModel } from '@mui/x-data-grid';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import { IUserAtom, user } from '../../recoil/atoms/user';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import MetricChart from '../components/Snapshot/Metrics/MetricChart';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { formatUtcDateToLocalString } from './dateUtils';

const useStyles = makeStyles((theme: Theme) => ({
  metricPanelContainerOutline: {
    width: '31%',
    maxWidth: '300px',
    minWidth: '220px',
  },
  metricPanelContainerBox: {
    padding: '0px',
    margin: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
  metricPanelTopRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  metricPanelTitle: {
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'start',
  },
  mainText: {
    color: theme.palette.mode === 'dark' ? '#fff' : 'black',
  },
  metricPanelBottomRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metricPanelBottomRowLeftColumnContainer: {},
  metricPanelBottomRowRightColumnContainer: {},
  metricPanelData: {
    fontWeight: 600,
    fontSize: '28px',
    marginBottom: '0px',
  },
  metricPanelUpwardArrow: {
    height: '36px',
    width: '36px',
    color: 'rgb(63,200,183)',
  },
  metricPanelDownwardArrow: {
    height: '36px',
    width: '36px',
    color: 'rgb(251,91,93)',
  },
  metricPanelHorizontalArrow: {
    height: '40px',
    width: '40px',
  },
  metricPanelDeltaContainerOutline: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  metricPanelArrowContainerOutline: {},
  metricPanelArrowIconContainerOutline: {
    width: '40px',
    display: 'flex',
    margin: '0px 8px',
    justifyContent: 'center',
  },
  metricPanelPercentageChangeContainerOutline: {},
  metricPanelPercentageChangeSubContainerOutline: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  metricPanelPercentageChange: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: '0px',
  },
  metricPanelText: {
    fontSize: '10px',
    fontWeight: 300,
    marginBottom: '0px',
  },
  questionMarkIcon: {
    borderRadius: '50%',
    height: '16px',
    width: '16px',
    padding: '2px',
  },
  metricPanelSkeletonContainerOutline: {
    width: '22%',
    maxWidth: '268px',
    minWidth: '220px',
    height: '96px',
  },
  metricPanelSkeletonContainerBox: {
    padding: '0px',
    margin: '16px',
  },
  skeleton: {
    backgroundColor: theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey',
    height: '6em',
    margin: 10,
  },
  fullWidth: {
    width: '100% !important',
  },
  idColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: '80px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  switchColumn: {
    display: 'flex',
    flexDirection: 'row',
    width: '100px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameColumn: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: '250px !important',
    width: '100% !important',
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'row',
    // alignItems: 'center',
    // justifyContent: 'center',
    // width: '100%'
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
}));

const getNoteToDisplay = (jsonArray: GridCellValue) => {
  return Array.isArray(jsonArray)
    ? jsonArray?.length > 1
      ? jsonArray?.[1]
      : jsonArray?.[0]
    : {};
};

export interface ITransferReportRow {
  id: string;
  buttonPressed: number;
  eventType: string;
  message: string;
  internalNotes: {
    [key: string]: string | number;
  }[];
  createdDate: string;
  totalEvents: number;
  transferAccepted: number;
  transferIgnored: number;
  transferRejected: number;
  transferComplete: number;
  transferInitiated: number;
}

const TransferReports = () => {
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [search, setSearch] = useState<string>('');
  const start = useRecoilValueLoadable<any>(startingDate);
  const end = useRecoilValueLoadable<any>(endingDate);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'createdDate', sort: 'desc' },
  ]);

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };

  //   const handleSearchChange = useCallback(
  //     (value: string) => {
  //       // reset page back to 1
  //       setPage(1);
  //       setSearch(value);
  //     },
  //     [setSearch]
  //   );

  const getTransferReportsEndpoint = () =>
    userAtomContents.businessid
      ? endpointEnum.GET_TRANSFER_ATTEMPTS.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        )
      : '';

  const classes = useStyles();
  const [_, transferReportsLoadable] = makeApiRequestCached(
    {
      endpoint: getTransferReportsEndpoint(),
      stage: 'itn',
      method: 'POST',
      domain: process.env.VITE_APP_API_DOMAIN,
      payloadJsonString: JSON.stringify({
        call: 'getTransferAttemptsByBusinessId',
        startdate: start.contents,
        enddate: end.contents,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
        startrow: (page - 1) * pageSize,
        rowsize: pageSize,
        search: search,
        sort: sortModel[0].field === 'id' ? 'externalId' : sortModel[0].field,
        sortDirection: sortModel[0].sort,
      }),
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );

  //   const transferReportsLoadable = useRecoilValueLoadable(
  //     makeAPIRequest({
  //       endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
  //       method: 'POST',
  //       payloadJsonString: JSON.stringify({
  //         call: 'v1/getBusinessPowerlists',
  //       }),
  //     })
  //   );
  const rows = useMemo<ITransferReportRow[]>(() => {
    try {
      let rowsToDisplay = JSON.parse(
        transferReportsLoadable?.contents?.response?.data?.result?.[0]?.rows ??
          '[]'
      );
      rowsToDisplay = rowsToDisplay.map((el, i) => {
        return {
          buttonPressed: el?.buttonPressed,
          id: el?.externalId,
          eventType: el?.eventType,
          message: el?.message,
          internalNotes: el?.internalNotes,
          createdDate: el?.createdDate,
          totalEvents: el?.totalEvents ?? 0,
          transferAccepted: el?.transferAccepted ?? 0,
          transferIgnored: el?.transferIgnored ?? 0,
          transferRejected: el?.transferRejected ?? 0,
          transferComplete: el?.transferComplete ?? 0,
          transferInitiated: el?.transferInitiated ?? 0,
        };
      });
      return rowsToDisplay;
    } catch (e) {
      return [];
    }
  }, [transferReportsLoadable]);

  const totalCount = useMemo<number>(() => {
    const count =
      transferReportsLoadable?.contents?.response?.data?.result?.[0]
        ?.totalCount ?? 0;
    return count;
  }, [transferReportsLoadable]);

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'createdDate',
        headerName: 'Transfer Date',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        valueFormatter: (params) => {
          return formatUtcDateToLocalString(params.value);
        },
      },
      {
        field: 'id',
        headerName: 'Call UUID',
        headerClassName: classes.idColumn,
        cellClassName: classes.idColumn,
        renderCell: (params) => (
          <a
            style={{ textAlign: 'left' }}
            href={`https://console.plivo.com/voice/logs/calls/stats/${
              params.value
            }/?end_date=${moment(+(params.row.calldate as string)).format(
              'MM/DD/YYYY'
            )}&subaccount=SANZVLNGY4NDFHYMZHMJ`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.value}
          </a>
        ),
      },
      {
        field: 'internalNotes',
        headerName: 'Transferring Agent',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) => {
          return (
            <>
              <div>
                {getNoteToDisplay(params?.value)?.transferringAgent ?? ''}
              </div>
            </>
          );
        },
        enableSorting: false,
      },
      {
        field: 'internalNotes',
        headerName: 'Transfer Type',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        enableSorting: false,
        renderCell: (params) => {
          return (
            <>
              <div>
                {getNoteToDisplay(params?.value)?.recipientAgent
                  ? 'Agent'
                  : getNoteToDisplay(params?.value)?.recipientQueue
                  ? 'Queue'
                  : ''}
              </div>
            </>
          );
        },
      },
      {
        field: 'internalNotes',
        enableSorting: false,
        headerName: 'Recipient Id',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) => {
          return (
            <>
              <div>
                {getNoteToDisplay(params?.value)?.recipientAgent
                  ? getNoteToDisplay(params?.value)?.recipientAgent
                  : getNoteToDisplay(params?.value)?.recipientQueue
                  ? getNoteToDisplay(params?.value)?.recipientQueue
                  : ''}
              </div>
            </>
          );
        },
      },
      {
        field: 'internalNotes',
        enableSorting: false,
        headerName: 'Customer Number',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) => {
          return (
            <>
              <div>
                {getNoteToDisplay(params?.value)?.customerNumber
                  ? getNoteToDisplay(params?.value)?.customerNumber
                  : ''}
              </div>
            </>
          );
        },
      },
      {
        field: 'transferInitiated',
        headerName: 'Initiated',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'buttonPressed',
        headerName: 'Button Pressed',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'transferAccepted',
        headerName: 'Accepted',
        headerClassName: classes.nameColumn,
        cellClassName: classes.nameColumn,
      },
      {
        field: 'transferIgnored',
        headerName: 'Ignored',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'transferRejected',
        headerName: 'Rejected',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        // renderCell: (params) => {
        //   return (
        //     <>
        //       <div>
        //         {typeof params.value === 'string' &&
        //           params.value.charAt(0).toUpperCase() + params.value.slice(1)}
        //       </div>
        //     </>
        //   );
        // },
      },
      {
        field: 'transferComplete',
        headerName: 'Completed',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        // renderCell: (params) => {
        //   return (
        //     <>
        //       <div>{`${params?.value ?? 0}` + ' Recipients'}</div>
        //     </>
        //   );
        // },
      },
    ],
    [classes, transferReportsLoadable]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setSearch]
  );

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        search={search}
        setSearch={handleSearchChange}
        isLoading={transferReportsLoadable?.state === 'loading'}
        hideVerticalDivider={true}
      />
    ),
    [search, rows]
  );

  const transferAttemptsData = useMemo(() => {
    try {
      const transferData =
        transferReportsLoadable?.contents?.response?.data?.result?.[0];
      const accepted = transferData?.totalAccepted ?? 0;
      const ignored = transferData?.totalIgnored ?? 0;
      const rejected = transferData?.totalRejected ?? 0;
      const failed = transferData?.totalFailed ?? 0;

      return {
        'Transfer Attempts': [
          {
            chart: 'Transfer Attempts',
            title: 'Accepted',
            data: accepted,
            displayData: accepted,
            loading: false,
            unit: '',
            order: 0,
          },
          {
            chart: 'Transfer Attempts',
            title: 'Ignored',
            data: ignored,
            displayData: ignored,
            loading: false,
            unit: '',
            order: 1,
          },
          {
            chart: 'Transfer Attempts',
            title: 'Rejected',
            data: rejected,
            displayData: rejected,
            loading: false,
            unit: '',
            order: 2,
          },
          {
            chart: 'Transfer Attempts',
            title: 'Failed',
            data: failed,
            displayData: failed,
            loading: false,
            unit: '',
            order: 3,
          },
        ],
      };
    } catch (e) {
      return {
        'Transfer Attempts': [
          {
            chart: 'Transfer Attempts',
            title: 'Accepted',
            data: 0,
            order: 0,
            loading: false,
            unit: '',
            displayData: 0,
          },
          {
            chart: 'Transfer Attempts',
            title: 'Ignored',
            data: 0,
            order: 1,
            loading: false,
            unit: '',
            displayData: 0,
          },
          {
            chart: 'Transfer Attempts',
            title: 'Rejected',
            data: 0,
            order: 2,
            loading: false,
            unit: '',
            displayData: 0,
          },
          {
            chart: 'Transfer Attempts',
            title: 'Failed',
            data: 0,
            displayData: 0,
            loading: false,
            unit: '',
            order: 3,
          },
        ],
      };
    }
  }, [transferReportsLoadable]);

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <MetricChart
          height={360}
          width={360}
          fullWidth={true}
          align={'right'}
          verticalAlign={'middle'}
          title={'Transfer Attempts'}
          key={0}
          colorArr={['green', '#91D2E8', '#0098C7', 'orange']}
          data={transferAttemptsData}
          loading={transferReportsLoadable.state === 'loading'}
        />
      </div>
      <Paper square={true} elevation={0}>
        <DataTable
          ToolbarComponent={ToolbarComponent}
          rows={rows ?? []}
          rowCount={totalCount ?? 0}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={setGridSortModel}
          pagination
          paginationMode="server"
          loading={transferReportsLoadable.state === 'loading'}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Paper>
    </div>
  );
};

export default TransferReports;
